import classnames from 'classnames'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import {
  TAG_LIST_DEFAULT_SIZE,
  TAG_LIST_DEFAULT_VISIBLE_COUNT,
} from '../MultiValuedList.constants'
import { MultiValuedListItems } from '../MultiValuedListItems'
import { MultiValuedListMore } from '../MultiValuedListMore'
import { MultiValuedListMoreItems } from '../MultiValuedListMoreItems'

import { useMultiValuedListResponsiveVisibleCount } from './MultiValuedList.hooks'
import { multiValuesListMoreItemsSort } from './MultiValuedList.utils'

import * as styles from './MultiValuedList.module.css'

function MultiValuedList({
  Item,
  Items = MultiValuedListItems,
  More = MultiValuedListMore,
  MoreItem = Item,
  MoreItems = MultiValuedListMoreItems,
  activeItem,
  className = styles.MultiValuedList,
  disabled,
  done,
  filter,
  floatingPortalId,
  itemProps = EMPTY_OBJECT,
  items = EMPTY_LIST,
  itemsCount = items.length,
  itemsProps,
  moreItemsProps,
  moreProps,
  size = TAG_LIST_DEFAULT_SIZE,
  sortMoreItems = multiValuesListMoreItemsSort,
  unread,
  variant,
  visibleCount = TAG_LIST_DEFAULT_VISIBLE_COUNT,
  onClickItem,
  onMouseDownItem,
  ...divProps
}) {
  const isResponsive = visibleCount === null

  const {
    containerRef,
    measurementRef,
    moreMeasureRef,
    visibleCount: responsiveVisibleCount,
  } = useMultiValuedListResponsiveVisibleCount({
    enabled: isResponsive,
    items,
  })

  const effectiveVisibleCount = isResponsive
    ? responsiveVisibleCount
    : visibleCount

  const listItems = items.slice(0, effectiveVisibleCount)
  const moreItems = sortMoreItems(items.slice(effectiveVisibleCount))
  const moreCount = Math.max(itemsCount - listItems.length, 0)
  const isSingle = moreCount === 0

  const mods = {
    ...(size ? { [styles[size]]: true } : null),
    [styles.responsive]: isResponsive,
  }

  const commonItemsProps = {
    ...itemsProps,
    Item,
    activeItem,
    disabled,
    done,
    isSingle,
    itemProps,
    size,
    unread,
    variant,
  }

  const commonMoreProps = {
    ...moreProps,
    activeItem,
    count: moreCount,
    disabled,
    floatingPortalId,
    items: moreItems,
    size,
    variant,
  }

  return items.length > 0 ? (
    <div
      {...divProps}
      className={classnames(className, mods)}
      ref={isResponsive ? containerRef : undefined}
    >
      <Items
        {...commonItemsProps}
        items={listItems}
        onClickItem={onClickItem}
        onMouseDownItem={onMouseDownItem}
      />

      {isSingle || moreCount === 0 ? null : (
        <More {...commonMoreProps}>
          {moreItems.length > 0 ? (
            <MoreItems
              {...moreItemsProps}
              Item={MoreItem}
              activeItem={activeItem}
              disabled={disabled}
              filter={filter}
              items={moreItems}
            />
          ) : null}
        </More>
      )}

      {isResponsive ? (
        <div aria-hidden className={styles.measurement} ref={measurementRef}>
          <Items {...commonItemsProps} items={items} />
          <More
            {...commonMoreProps}
            buttonProps={{
              ...moreProps?.buttonProps,
              ref: moreMeasureRef,
            }}
          />
        </div>
      ) : null}
    </div>
  ) : null
}

export default MultiValuedList
